import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import { AiSkillDto, AiSkillKind } from '@travel-planner/library-travel-planner-api';
import { LoadingState } from '../../../../core/models/common';
import { AiSkillsActions } from './ai-skills.actions';

export interface AiSkillListItem extends AiSkillDto {
    childCount: number;
    usedSkillCount: number;
}

export interface AiSkillGroup {
    kind: AiSkillKind;
    skills: AiSkillListItem[];
}

export interface AiSkillsState extends EntityState<AiSkillDto> {
    loadingState: LoadingState;
    savingKeys: string[];
    resettingKeys: string[];
}

export const aiSkillsAdapter: EntityAdapter<AiSkillDto> = createEntityAdapter<AiSkillDto>({
    selectId: skill => skill.key,
    sortComparer: (a, b) => a.key.localeCompare(b.key)
});

const initialState: AiSkillsState = aiSkillsAdapter.getInitialState({
    loadingState: 'Initial',
    savingKeys: [],
    resettingKeys: [],
});

const groupOrder: AiSkillKind[] = ['WorkflowSkill', 'ModelStepSkill', 'ActionSkill'];

export const aiSkillsFeature = createFeature({
    name: 'aiSkills',
    reducer: createReducer(
        initialState,
        on(AiSkillsActions.loadAiSkills, (state) => ({
            ...state,
            loadingState: 'Loading' as const,
        })),
        on(AiSkillsActions.loadAiSkillsSuccess, (state, { skills }) => ({
            ...aiSkillsAdapter.setAll(skills, state),
            loadingState: 'Loaded' as const,
        })),
        on(AiSkillsActions.updateAiSkillConfiguration, (state, { key }) => ({
            ...state,
            savingKeys: state.savingKeys.includes(key) ? state.savingKeys : [...state.savingKeys, key],
        })),
        on(AiSkillsActions.updateAiSkillConfigurationSuccess, (state, { skill }) => ({
            ...aiSkillsAdapter.setOne(skill, state),
            loadingState: 'Loaded' as const,
            savingKeys: state.savingKeys.filter(key => key !== skill.key),
        })),
        on(AiSkillsActions.resetAiSkillConfiguration, (state, { key }) => ({
            ...state,
            resettingKeys: state.resettingKeys.includes(key) ? state.resettingKeys : [...state.resettingKeys, key],
        })),
        on(AiSkillsActions.resetAiSkillConfigurationSuccess, (state, { skill }) => ({
            ...aiSkillsAdapter.setOne(skill, state),
            loadingState: 'Loaded' as const,
            resettingKeys: state.resettingKeys.filter(key => key !== skill.key),
        })),
        on(AiSkillsActions.loadAiSkillsFailure, (state) => ({
            ...state,
            loadingState: 'Error' as const,
        })),
        on(AiSkillsActions.updateAiSkillConfigurationFailure, (state, { key }) => ({
            ...state,
            savingKeys: state.savingKeys.filter(savingKey => savingKey !== key),
        })),
        on(AiSkillsActions.resetAiSkillConfigurationFailure, (state, { key }) => ({
            ...state,
            resettingKeys: state.resettingKeys.filter(resettingKey => resettingKey !== key),
        })),
    ),
});

const { selectAiSkillsState } = aiSkillsFeature;

const selectSafeAiSkillsState = createSelector(
    selectAiSkillsState,
    state => state ?? initialState
);

const { selectAll, selectEntities } = aiSkillsAdapter.getSelectors(selectSafeAiSkillsState);

export const selectAiSkills = selectAll;

export const selectAiSkillEntities = selectEntities;

export const selectAiSkillsLoadingState = createSelector(
    selectSafeAiSkillsState,
    state => state.loadingState
);

export const selectAiSkillsSavingKeys = createSelector(
    selectSafeAiSkillsState,
    state => state.savingKeys
);

export const selectAiSkillsResettingKeys = createSelector(
    selectSafeAiSkillsState,
    state => state.resettingKeys
);

export const selectAiSkillsIsSaving = (key: string | null) => createSelector(
    selectAiSkillsSavingKeys,
    savingKeys => !!key && savingKeys.includes(key)
);

export const selectAiSkillsIsResetting = (key: string | null) => createSelector(
    selectAiSkillsResettingKeys,
    resettingKeys => !!key && resettingKeys.includes(key)
);

export const selectAiSkill = (key: string | null) => createSelector(
    selectAiSkillEntities,
    entities => key ? entities[key] ?? null : null
);

export const selectAiSkillChildren = (parentKey: string | null) => createSelector(
    selectAiSkills,
    skills => parentKey
        ? skills
            .filter(skill => skill.parentSkillKey === parentKey && skill.kind === 'ModelStepSkill')
            .sort((a, b) => a.key.localeCompare(b.key))
        : []
);

export const selectAiSkillUsedSkills = (key: string | null) => createSelector(
    selectAiSkill(key),
    selectAiSkillEntities,
    (skill, entities) => skill
        ? (skill.usedSkillKeys ?? [])
            .map(usedSkillKey => entities[usedSkillKey])
            .filter((usedSkill): usedSkill is AiSkillDto => !!usedSkill)
        : []
);

export const selectAiSkillUsedBySkills = (key: string | null) => createSelector(
    selectAiSkills,
    skills => key
        ? skills
            .filter(skill => (skill.usedSkillKeys ?? []).includes(key))
            .sort((a, b) => a.key.localeCompare(b.key))
        : []
);

export const selectAiSkillGroups = createSelector(
    selectAiSkills,
    skills => {
        const childCountByParent = skills.reduce((acc, skill) => {
            if (!skill.parentSkillKey) {
                return acc;
            }

            acc.set(skill.parentSkillKey, (acc.get(skill.parentSkillKey) ?? 0) + 1);
            return acc;
        }, new Map<string, number>());

        return groupOrder.map(kind => ({
            kind,
            skills: skills
                .filter(skill => skill.kind === kind)
                .map(skill => ({
                    ...skill,
                    childCount: childCountByParent.get(skill.key) ?? 0,
                    usedSkillCount: skill.usedSkillKeys?.length ?? 0,
                }))
        }));
    }
);
